<template>
  <div class="card-body px-0 pt-0 pb-2">
    <div class="table-responsive p-0">
      <table class="table main-table">
        <thead>
          <tr>
            <th> Category </th>
            <th> Code </th>
            <th> Name </th>
            <th> Description</th>
            <th> SF Sharing (Php)</th>
            <th> Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="7">
              <div class="text-center">
                <Vue3Lottie :animationData="searchingJSON" class="height-150 py-2" />
              </div>
            </td>
          </tr>
          <tr v-if="!isLoading && pageItems.length == 0">
            <td colspan="7">
              <div class="text-center">
                <Vue3Lottie :animationData="noDataJSON" class="height-150 py-2" />
                <p class="text-xs font-weight-bold">No Data Found</p>
              </div>
            </td>
          </tr>
          <tr v-for="(category, index) in pageItems" :key="index">
            <td>
              <p class="sub-text">
                {{ category.category }}
              </p>
            </td>
            <td>
              <p class="main-text">
                {{ category.code }}
              </p>
            </td>
            <td>
              <div class="d-flex align-items-center">
                <img :src="category.logo" style="width: 40px; margin-right: 20px" />
                <p class="sub-text"> {{ category.name }}</p>
              </div>
            </td>
            <td class="align-middle text-sm">
              {{ category.description ? category.description : "-" }}
            </td>
            <td class="align-middle text-sm">
              <input
                v-if="editingBiller[category.code]"
                type="number"
                step="0.01"
                min="0"
                class="sf-sharing-input"
                v-model="category.sfSharing"
                :ref="'input-' + category.code"
                placeholder="0.00"
              />
              <span v-else class="sub-text">
                {{ category.sfSharing ? parseFloat(category.sfSharing).toFixed(2) : '0.00' }}
              </span>
              <button
                v-if="editingBiller[category.code]"
                @click="saveSfSharing(category)"
                class="sf-action-btn check-btn"
                title="Save"
              >
                <i class="fas fa-check"></i>
              </button>
              <button
                v-if="editingBiller[category.code]"
                @click="cancelEdit(category)"
                class="sf-action-btn cancel-btn"
                title="Cancel"
              >
                <i class="fas fa-times"></i>
              </button>
              <button
                v-if="!editingBiller[category.code]"
                @click="enableEdit(category)"
                class="sf-action-btn edit-btn"
                title="Edit"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
            </td>
            <td>
              <label class="switch">
                <input type="checkbox" v-model="category.isActive" @change="handleChange(category)" />
                <span class="slider round"></span>
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="paginate-div d-flex align-items-end mt-3 me-3">
      <vue-awesome-paginate class="ms-auto" :max-pages-shown="5" :total-items="billers.length"
        :items-per-page="itemPerPage" :on-click="onPageChanged" v-model="currentPage" />
    </div>
  </div>

  <GlobalModal :show="modal.show" :title="modal.title" :message="modal.message" :type="modal.type"
    @confirm="modal.onConfirm" @cancel="modal.onCancel" @close="modal.show = false" />
</template>

<script>
import { mapActions, mapState } from "vuex";
import noDataJSON from "./../../../assets/no-data.json";
import searchingJSON from "./../../../assets/searching.json";
import GlobalModal from "@/components/GlobalModal.vue";

export default {
  data() {
    return {
      noDataJSON,
      searchingJSON,
      itemPerPage: 20,
      pageItems: [],
      currentPage: 1,
      modal: { show: false, type: 'success', title: '', message: '' },
      originalSfSharing: {},
      editingBiller: {}
    };
  },
  components: {
    GlobalModal
  },
  watch: {
    billers: {
      handler(newValue, oldValue) {
        // Only trigger page change if the array length changed or it's the initial load
        // Don't trigger on property changes (like sfSharing updates)
        if (!oldValue || newValue.length !== oldValue.length) {
          this.onPageChanged(1);
        }
      },
      deep: false, // Changed to false to prevent triggering on property changes
    },
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.billerController.isLoading,
      billers: (state) => state.billerController.filteredBiller,
    }),
  },
  methods: {
    ...mapActions({
      updateBillerStatus: "billerController/UpdateBillerStatus",
      updateBillerSfSharing: "billerController/UpdateBillerSfSharing",
    }),
    handleChange: async function (category) {
      const action = category.isActive ? 'activate' : 'deactivate';
      category.isActive = !category.isActive;

      const confirmed = await this.showModal(
        'confirmation',
        'Change Biller Visibility',
        `Are you sure you want to ${action} this biller?`
      );
      if (confirmed) {
        try {
          var result = await this.updateBillerStatus({
            code: category.code,
            status: !category.isActive,
          });

          if (result) {
            category.isActive = !category.isActive;
          }
        } catch (error) {
          this.showModal(
            'error',
            'An error occurred while adding rate.',
          );
        }
      }
    },
    onPageChanged(page) {
      //this.setCurrentPage(page);
      this.pageItems = this.billers.slice(
        (this.currentPage - 1) * this.itemPerPage,
        this.currentPage * this.itemPerPage
      );
      // Store original SF sharing values when page changes
      this.pageItems.forEach(item => {
        // Only initialize if not already set
        if (this.originalSfSharing[item.code] === undefined) {
          this.originalSfSharing[item.code] = item.sfSharing;
        }
        // Only set to false if not already editing
        if (this.editingBiller[item.code] === undefined) {
          this.editingBiller[item.code] = false;
        }
      });
    },
    enableEdit(biller) {
      // Store original value before editing
      this.originalSfSharing[biller.code] = biller.sfSharing || 0;
      // Enable edit mode
      this.editingBiller[biller.code] = true;
      // Focus input after Vue updates the DOM
      this.$nextTick(() => {
        const input = this.$refs['input-' + biller.code];
        if (input && input[0]) {
          input[0].focus();
          input[0].select();
        }
      });
    },
    cancelEdit(biller) {
      // Revert to original value
      biller.sfSharing = this.originalSfSharing[biller.code];
      // Disable edit mode
      this.editingBiller[biller.code] = false;
    },
    async saveSfSharing(biller) {
      const originalValue = this.originalSfSharing[biller.code];
      // Convert string to number and handle empty/invalid input
      const newValue = biller.sfSharing === '' || biller.sfSharing === null || biller.sfSharing === undefined
        ? 0
        : parseFloat(biller.sfSharing);

      console.log('=== SAVE SF SHARING DEBUG ===');
      console.log('Biller Code:', biller.code);
      console.log('Original Value:', originalValue);
      console.log('New Value:', newValue);
      console.log('Raw Input Value:', biller.sfSharing);
      console.log('Type of new value:', typeof newValue);

      // Check if the value is valid
      if (isNaN(newValue)) {
        console.error('Invalid value - NaN detected');
        this.showModal(
          'error',
          'Invalid Value',
          'Please enter a valid number.'
        );
        return;
      }

      // Only update if value has changed
      if (originalValue !== newValue) {
        console.log('Value changed - calling API...');
        try {
          const payload = {
            code: biller.code,
            sfSharing: newValue
          };
          console.log('API Payload:', JSON.stringify(payload));

          const result = await this.updateBillerSfSharing(payload);

          console.log('API Response:', result);
          console.log('Result type:', typeof result);
          console.log('Result value:', result);

          if (result !== false && result !== null) {
            console.log('Update successful - updating UI');
            // Update the biller object with the parsed value
            biller.sfSharing = newValue;
            // Update stored original value
            this.originalSfSharing[biller.code] = newValue;
            // Disable edit mode
            this.editingBiller[biller.code] = false;
            // Show success message briefly
            await this.showModal(
              'success',
              'SF Sharing Updated',
              `SF Sharing for ${biller.name} has been updated to ${newValue.toFixed(2)}`
            );
          } else {
            console.error('Update failed - result was false/null');
            throw new Error('Update returned false');
          }
        } catch (error) {
          console.error('=== ERROR UPDATING SF SHARING ===');
          console.error('Error object:', error);
          console.error('Error message:', error.message);
          console.error('Error response:', error.response);
          // Revert to original value on error
          biller.sfSharing = originalValue;
          this.showModal(
            'error',
            'Update Failed',
            'An error occurred while updating SF Sharing. Check console for details.'
          );
        }
      } else {
        console.log('No value change - just closing edit mode');
        // Just disable edit mode if no changes
        this.editingBiller[biller.code] = false;
      }
      console.log('=== END SAVE SF SHARING DEBUG ===');
    },
    showModal(type, title, message) {
      return new Promise((resolve) => {
        this.modal = {
          show: true,
          type,
          title,
          message,
          onConfirm: () => {
            this.modal.show = false;
            resolve(true);
          },
          onCancel: () => {
            this.modal.show = false;
            resolve(false);
          },
        };
      });
    }
  },
};
</script>

<style scoped>
/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 25px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked+.slider {
  background-color: #2196f3;
}

input:focus+.slider {
  box-shadow: 0 0 1px #2196f3;
}

input:checked+.slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

/* SF Sharing Input */
.sf-sharing-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #d2d6da;
  border-radius: 4px;
  font-size: 0.875rem;
  text-align: right;
  transition: border-color 0.3s ease;
  margin-right: 8px;
}

.sf-sharing-input:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.sf-sharing-input:hover {
  border-color: #a0a4a8;
}

/* Remove spinner arrows for number input in Chrome, Safari, Edge */
.sf-sharing-input::-webkit-outer-spin-button,
.sf-sharing-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove spinner arrows for number input in Firefox */
.sf-sharing-input[type=number] {
  -moz-appearance: textfield;
}

/* Action Buttons */
.sf-action-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  margin-left: 4px;
}

.edit-btn {
  background-color: #f0f2f5;
  color: #5e72e4;
}

.edit-btn:hover {
  background-color: #5e72e4;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(94, 114, 228, 0.3);
}

.check-btn {
  background-color: #2dce89;
  color: white;
}

.check-btn:hover {
  background-color: #24a46d;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(45, 206, 137, 0.4);
}

.cancel-btn {
  background-color: #f5365c;
  color: white;
}

.cancel-btn:hover {
  background-color: #d42c4a;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(245, 54, 92, 0.4);
}

.sf-action-btn:active {
  transform: translateY(0);
}
</style>